import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Alert, Dropdown, Modal, Input, Select, Typography } from '@/shared/ui/primitives';
import { PlusOutlined, DownOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import RoomAdTable from '../components/RoomAdTable';
import { useDeleteRoomAd } from '../hooks/useRoomAdMutations';
import { useBulkAdDelete, useBulkAdStatus, useBulkAdFlags } from '@/features/ops/hooks/useOps';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { userApi } from '@/features/users/api/user.api';
import { nationalityApi } from '@/features/nationalities/api/nationality.api';
import { propertyAmenityApi } from '@/features/property-amenities/api/propertyAmenity.api';
import apiClient from '@/lib/apiClient';

const RESOURCE = ADMIN_FILTER_RESOURCES.roomAds;

type BulkStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'REJECTED';

const FLAG_FIELDS = [
  { key: 'showPhoneNumber', labelKey: 'roomAds.showPhone' },
  { key: 'showWhatsappContact', labelKey: 'roomAds.showWhatsapp' },
  { key: 'showOwnerInfo', labelKey: 'roomAds.showOwnerInfo' },
  { key: 'showInAppMessaging', labelKey: 'roomAds.showInAppMessaging' },
  { key: 'hideExactLocation', labelKey: 'roomAds.hideLocation' },
  { key: 'isFeatured', labelKey: 'roomAds.featured' },
];

export default function RoomAdListPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.toLowerCase().startsWith('ar');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<BulkStatus | null>(null);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkFlagsOpen, setBulkFlagsOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [flagValues, setFlagValues] = useState<Record<string, boolean | undefined>>({});

  const list = useDynamicAdminList<any>(RESOURCE, { onFiltersChanged: () => setSelectedIds([]) });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteRoomAd();
  const bulkStatusMutation = useBulkAdStatus();
  const bulkDeleteMutation = useBulkAdDelete();
  const bulkFlagsMutation = useBulkAdFlags();

  const relationLookups = useMemo(() => ({
    'user.id': {
      fetchFn: ({ search, page, size }: any) => userApi.list({ page, size, firstName: search }),
      fetchById: (id: string | number) => userApi.getById(id),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || `#${u.id}` }),
      placeholder: t('common.user'),
    },
    'user.nationality.id': {
      fetchFn: ({ search, page, size }: any) => nationalityApi.list({ page, size, countryNameEn: search }),
      fetchById: (id: string | number) => nationalityApi.getById(id),
      mapOption: (n: any) => ({ value: n.id, label: n.countryNameEn || n.nameEn || n.countryNameAr || `#${n.id}` }),
      placeholder: t('filters.nationality'),
    },
    'amenities.id': {
      fetchFn: ({ search, page, size }: any) => propertyAmenityApi.list({ page, size, nameEn: search }),
      fetchById: (id: string | number) => propertyAmenityApi.getById(id),
      mapOption: (a: any) => ({ value: a.id, label: isArabic ? a.nameAr || a.nameEn || `#${a.id}` : a.nameEn || a.nameAr || `#${a.id}` }),
      placeholder: t('roomAds.amenities'),
    },
    'address.geoCityId': {
      fetchFn: ({ search, page, size }: any) => fetchPublicGeoList('/public/geo/cities', { search, page, size }),
      mapOption: (c: any) => ({ value: c.id, label: geoName(c, isArabic) }),
      placeholder: t('common.city'),
    },
    'address.geoDistrictId': {
      fetchFn: ({ search, page, size }: any) => fetchPublicGeoList('/public/geo/districts', { search, page, size }),
      mapOption: (d: any) => ({ value: d.id, label: geoName(d, isArabic) }),
      placeholder: t('common.district'),
    },
  }), [isArabic, t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  const confirmBulkStatus = () => {
    if (!bulkStatus) return;
    bulkStatusMutation.mutate(
      { adIds: selectedIds, status: bulkStatus, rejectReason: rejectReason.trim() || undefined },
      { onSettled: () => { setBulkStatus(null); setRejectReason(''); setSelectedIds([]); } },
    );
  };

  const confirmBulkDelete = () => {
    bulkDeleteMutation.mutate(selectedIds, {
      onSettled: () => { setBulkDelete(false); setSelectedIds([]); },
    });
  };

  const confirmBulkFlags = () => {
    const flags: Record<string, boolean | undefined> = {};
    for (const [k, v] of Object.entries(flagValues)) {
      if (v !== undefined) flags[k] = v;
    }
    bulkFlagsMutation.mutate(
      { adIds: selectedIds, flags },
      { onSettled: () => { setBulkFlagsOpen(false); setFlagValues({}); setSelectedIds([]); } },
    );
  };

  const bulkMenu = {
    items: [
      { key: 'publish', label: t('adOps.bulk.publish'), onClick: () => setBulkStatus('PUBLISHED') },
      { key: 'reject', label: t('adOps.bulk.reject'), onClick: () => setBulkStatus('REJECTED') },
      { key: 'archive', label: t('adOps.bulk.archive'), onClick: () => setBulkStatus('ARCHIVED') },
      { key: 'flags', label: t('adOps.bulk.flags'), onClick: () => setBulkFlagsOpen(true) },
      { type: 'divider' as const },
      { key: 'delete', label: t('adOps.bulk.deletePermanently'), danger: true, onClick: () => setBulkDelete(true) },
    ],
  };

  const flagSelectOptions = [
    { value: '__unchanged__', label: t('adOps.bulk.flagUnchanged') },
    { value: 'true', label: t('common.yes') },
    { value: 'false', label: t('common.no') },
  ];

  return (
    <div>
      <PageHeader
        title={t('roomAds.title')}
        subtitle={t('roomAds.subtitle')}
        action={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/room-ads/new')}>
            {t('roomAds.create')}
          </Button>
        }
      />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data}
        loading={list.metadataQuery.isLoading}
        value={list.filters}
        relationLookups={relationLookups}
        onChange={list.setFilters}
        onReset={list.resetFilters}
      />

      {selectedIds.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('adOps.bulk.selected', { count: selectedIds.length })}
          action={
            <Space>
              <Dropdown menu={bulkMenu} trigger={['click']}>
                <Button type="primary">{t('adOps.bulk.action')} <DownOutlined /></Button>
              </Dropdown>
              <Button onClick={() => setSelectedIds([])}>{t('userOps.bulk.clear')}</Button>
            </Space>
          }
        />
      )}

      <div style={{ marginTop: 16 }}>
        <RoomAdTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onView={(id) => navigate(`/room-ads/${id}`)}
          onEdit={(id) => navigate(`/room-ads/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('roomAds.deleteTitle')}
        message={`${t('roomAds.deleteMessage')} #${deleteTarget?.id} "${deleteTarget?.title || ''}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />

      <Modal
        open={!!bulkStatus}
        onCancel={() => { setBulkStatus(null); setRejectReason(''); }}
        onOk={confirmBulkStatus}
        okText={bulkStatus === 'REJECTED'
          ? t('adOps.bulk.rejectAll')
          : bulkStatus
            ? t('adOps.bulk.setStatus', { status: t(`roomAds.${bulkStatus}`, bulkStatus) })
            : ''}
        okButtonProps={{ danger: bulkStatus === 'REJECTED', loading: bulkStatusMutation.isPending }}
        title={bulkStatus
          ? t('adOps.bulk.title', { status: t(`roomAds.${bulkStatus}`, bulkStatus), count: selectedIds.length })
          : ''}
      >
        {bulkStatus === 'REJECTED' && (
          <Input.TextArea
            rows={3}
            placeholder={t('adOps.bulk.rejectReasonPlaceholder')}
            value={rejectReason}
            onChange={(e: any) => setRejectReason(e.target.value)}
          />
        )}
        {bulkStatus !== 'REJECTED' && <p>{t('adOps.bulk.statusHint', { count: selectedIds.length })}</p>}
      </Modal>

      <Modal
        open={bulkDelete}
        onCancel={() => setBulkDelete(false)}
        onOk={confirmBulkDelete}
        okText={t('adOps.bulk.deletePermanently')}
        okButtonProps={{ danger: true, loading: bulkDeleteMutation.isPending }}
        title={t('adOps.bulk.deleteTitle', { count: selectedIds.length })}
      >
        <p>{t('adOps.bulk.deleteWarn', { count: selectedIds.length })}</p>
      </Modal>

      <Modal
        open={bulkFlagsOpen}
        onCancel={() => { setBulkFlagsOpen(false); setFlagValues({}); }}
        onOk={confirmBulkFlags}
        okText={t('adOps.bulk.applyFlags')}
        okButtonProps={{ loading: bulkFlagsMutation.isPending }}
        title={t('adOps.bulk.flagsTitle', { count: selectedIds.length })}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {FLAG_FIELDS.map(({ key, labelKey }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Typography.Text style={{ minWidth: 160 }}>{t(labelKey)}</Typography.Text>
              <Select
                style={{ flex: 1 }}
                value={flagValues[key] === undefined ? '__unchanged__' : String(flagValues[key])}
                onChange={(v: string) => setFlagValues((prev) => ({
                  ...prev,
                  [key]: v === '__unchanged__' ? undefined : v === 'true',
                }))}
                options={flagSelectOptions}
              />
            </div>
          ))}
        </Space>
      </Modal>
    </div>
  );
}

function geoName(item: any, isArabic: boolean) {
  if (isArabic) return item.nameAr || item.name || item.nameEn || `#${item.id}`;
  return item.nameEn || item.name || item.nameAr || `#${item.id}`;
}

async function fetchPublicGeoList(endpoint: string, params: { search?: string; page: number; size: number }) {
  const res = await apiClient.get(endpoint, {
    params: {
      page: params.page,
      size: params.size,
      search: params.search || undefined,
    },
  });
  const data = res.data?.data ?? {};
  const content = Array.isArray(data.content) ? data.content : [];
  return {
    content,
    totalPages: data.totalPages ?? 1,
    totalItems: data.totalItems ?? data.totalElements ?? content.length,
    currentPage: data.currentPage ?? params.page,
  };
}
